import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import customerService, { type Customer } from "@/services/customer";
import reportService, { type SalesReturnReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type SalesReturnReportFilters = {
  warehouse_id: number | null;
  customer_id: number | null;
  status: string;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): SalesReturnReportFilters => ({
  warehouse_id: null,
  customer_id: null,
  status: "",
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function useSalesReturnReport() {
  const { t } = useI18n();
  const items = ref<SalesReturnReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const customers = ref<Customer[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<SalesReturnReportFilters>(createInitialFilters());
  const i18nScope = "sales_return_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
    customer_id: filters.customer_id || undefined,
    status: filters.status || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, customerResults] = await Promise.all([
        warehouseService.getWarehouses(),
        customerService.getCustomers(),
      ]);

      warehouses.value = warehouseResults;
      customers.value = customerResults;
    } catch (error) {
      console.error("Error loading sales return report filters:", error);
    }
  };

  const fetchSalesReturnReports = async () => {
    loading.value = true;
    try {
      const response = await reportService.getSalesReturnReports(queryParams.value);
      items.value = response.data;
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching sales return reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchSalesReturnReports();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchSalesReturnReports();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchSalesReturnReports();
  };

  const exportSalesReturnReports = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportSalesReturnReports({
        search_query: searchQuery.value,
        warehouse_id: filters.warehouse_id || undefined,
        customer_id: filters.customer_id || undefined,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `sales_return_report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting sales return reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchSalesReturnReports();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchSalesReturnReports();
  });

  return {
    pageTitle,
    i18nScope,
    items,
    warehouses,
    customers,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportSalesReturnReports,
  };
}
